import { Card, Col, Row } from "reactstrap";
import React, { useEffect, useRef, useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { handleRichTextOutput } from "../../Common/HandleRichTextOutput";
import { useDispatch, useSelector } from "react-redux";
import RichTextEditor from "../../RichTextEditor";
import AppModal from "../../Common/AppModal";
import {
  createEmailTemplate,
  createEmailTemplateSuccess,
  emailTemplateDataSuccess,
  emailTemplateList,
  updateEmailTemplate,
  updateEmailTemplateSuccess,
} from "../../../store/CRMitEmailInbox/action";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

// duplicateFrom (Change #100363): a template row to copy. The modal opens in
// create mode already filled in ("Copy of ..."); nothing is saved until Create.
const CreateEditEmailTemplate = ({ modal, toggle, isEdit, duplicateFrom = null }) => {
  const dispatch = useDispatch();
  const isDuplicate = !isEdit && !!duplicateFrom;
  useEffect(() => {
    setIsClearEditor(true);
  }, []);
  const { emailTemplateDataAPI } = useSelector((state) => ({
    emailTemplateDataAPI:
      state.EmailInbound.emailTemplateData?.emailtemplate_data,
  }));

  //on close component clear data
  useEffect(() => {
    if (!modal) {
      setData(InitialData);
    }
    dispatch(emailTemplateDataSuccess([]));
  }, [modal]);

  const InitialData = {
    Title: "",
    Description: "",
    FromEmail: "",
    FromName: "",
    ReplyTo: "",
    Subject: "",
    BodyHtml: "",
    // Change #100658 - lock From Name / From Email when this template is used.
    FlagSenderReadOnly: 0,
  };
  const [data, setData] = useState(
    isDuplicate
      ? {
          ...InitialData,
          Title: `Copy of ${duplicateFrom?.Title || "template"}`,
          Description: duplicateFrom?.Description || "",
          FromEmail: duplicateFrom?.FromEmail || "",
          FromName: duplicateFrom?.FromName || "",
          Subject: duplicateFrom?.Subject || "",
          BodyHtml: duplicateFrom?.BodyHtml || "",
          ReplyTo: duplicateFrom?.ReplyTo || "",
          FlagSenderReadOnly: Number(duplicateFrom?.FlagSenderReadOnly) ? 1 : 0,
        }
      : InitialData
  );

  // The editor is cleared once just after the modal opens (isClearEditor), so
  // a body handed over at open would be wiped. Hand the copied body to the
  // editor only after that clear has run.
  const clearedOnceRef = useRef(false);
  const [duplicateBody, setDuplicateBody] = useState(null);

  useEffect(() => {
    if (isEmpty(emailTemplateDataAPI)) return;
    if (isEdit) {
      setData({
        Id: emailTemplateDataAPI?.Id,
        Title: emailTemplateDataAPI?.Title || "",
        Description: emailTemplateDataAPI?.Description || "",
        FromEmail: emailTemplateDataAPI?.FromEmail || "",
        FromName: emailTemplateDataAPI?.FromName || "",
        ReplyTo: emailTemplateDataAPI?.ReplyTo || "",
        Subject: emailTemplateDataAPI?.Subject || "",
        BodyHtml: emailTemplateDataAPI?.BodyHtml || "",
        FlagSenderReadOnly: Number(emailTemplateDataAPI?.FlagSenderReadOnly)
          ? 1
          : 0,
      });
    } else if (!isDuplicate) {
      setData(InitialData);
    }
  }, [isEdit, emailTemplateDataAPI]);
  const [isClearEditor, setIsClearEditor] = useState(false);
  useEffect(() => {
    if (isClearEditor) {
      clearedOnceRef.current = true;
    } else if (clearedOnceRef.current && isDuplicate && duplicateBody === null) {
      setDuplicateBody(duplicateFrom?.BodyHtml || "");
    }
  }, [isClearEditor]);
  const handleCreate = () => {
    dispatch(createEmailTemplate([data]));
  };
  const handleUpdate = () => {
    dispatch(updateEmailTemplate([data]));
  };
  const {
    emailTemplateCreateAPI,
    emailTemplateUpdateAPI,
    emailTemplateCreateLoading,
    emailTemplateUpdateLoading,
  } = useSelector((state) => ({
    emailTemplateCreateAPI: state.EmailInbound.emailTemplateCreate,
    emailTemplateUpdateAPI: state.EmailInbound.emailTemplateUpdate,
    emailTemplateCreateLoading: state.EmailInbound.emailTemplateCreateLoading,
    emailTemplateUpdateLoading: state.EmailInbound.emailTemplateUpdateLoading,
  }));
  useEffect(() => {
    if (isEmpty(emailTemplateCreateAPI)) return;
    if (emailTemplateCreateAPI?.status === 200) {
      toast.success(emailTemplateCreateAPI?.message);
      dispatch(emailTemplateList());
      toggle();
    } else {
      toast.error(emailTemplateCreateAPI?.message);
    }

    dispatch(createEmailTemplateSuccess([]));
  }, [emailTemplateCreateAPI]);

  useEffect(() => {
    if (isEmpty(emailTemplateUpdateAPI)) return;
    if (emailTemplateUpdateAPI?.status === 200) {
      toast.success(emailTemplateUpdateAPI?.message);
      dispatch(emailTemplateList());
      toggle();
    } else {
      toast.error(emailTemplateUpdateAPI?.message);
    }

    dispatch(updateEmailTemplateSuccess([]));
  }, [emailTemplateUpdateAPI]);
  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={`${isEdit ? "Update" : isDuplicate ? "Duplicate" : "Create"} Email Template`}
      icon={<EmailOutlinedIcon />}
      headerAccent="indigo"
      size="lg"
      loading={emailTemplateCreateLoading || emailTemplateUpdateLoading}
      loadingText="Saving…"
      submitLabel={isEdit ? "Update" : "Create"}
      submitColor="success"
      onSubmit={isEdit ? handleUpdate : handleCreate}
    >
      <Row>
            <Col lg={12}>
              <div>Title</div>
              <div>
                <input
                  type={"text"}
                  className={"form-control"}
                  value={data?.Title}
                  onChange={(e) => setData({ ...data, Title: e.target.value })}
                />
              </div>
              <div>Description</div>
              <div>
                <input
                  type={"text"}
                  className={"form-control"}
                  value={data?.Description}
                  onChange={(e) =>
                    setData({ ...data, Description: e.target.value })
                  }
                />
              </div>

              <div>Subject:</div>
              <div>
                <input
                  type={"text"}
                  className={"form-control"}
                  value={data?.Subject}
                  onChange={(e) =>
                    setData({ ...data, Subject: e.target.value })
                  }
                />
              </div>

              <div className={"mt-1"}>
                <div>Email Template</div>
                <RichTextEditor
                  autoFocus={false}
                  initialValue={data.BodyHtml}
                  placeholder={
                    isEdit ? data.BodyHtml : `Create Task Description`
                  }
                  // Change #100523 - functional update: the editor fires this
                  // with a stale `data` when the loaded body lands, which
                  // blanked Title/Subject/From (and saved them blank).
                  handleContentChange={(e) => {
                    setData((prev) => ({ ...prev, BodyHtml: e }));
                  }}
                  receivedContent={
                    isDuplicate ? duplicateBody : emailTemplateDataAPI?.BodyHtml
                  }
                  isClearEditor={isClearEditor}
                  setIsClearEditor={setIsClearEditor}
                />
              </div>
              <hr />
              <div>From Email:</div>
              <div>
                <input
                  type={"text"}
                  className={"form-control"}
                  value={data?.FromEmail}
                  onChange={(e) =>
                    setData({ ...data, FromEmail: e.target.value })
                  }
                />
              </div>
              <div>From Name:</div>
              <div>
                <input
                  type={"text"}
                  className={"form-control"}
                  value={data?.FromName}
                  onChange={(e) =>
                    setData({ ...data, FromName: e.target.value })
                  }
                />
              </div>
              <div>Reply To:</div>
              <div>
                <input
                  type={"text"}
                  className={"form-control"}
                  value={data?.ReplyTo || ""}
                  onChange={(e) =>
                    setData({ ...data, ReplyTo: e.target.value })
                  }
                />
              </div>

              {/* Change #100658 - some emails must always come from a set
                  address, so the sender can be locked in the compose window. */}
              <div className="form-check form-switch mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="emailTemplateFlagSenderReadOnly"
                  checked={Number(data?.FlagSenderReadOnly) === 1}
                  onChange={(e) =>
                    setData({
                      ...data,
                      FlagSenderReadOnly: e.target.checked ? 1 : 0,
                    })
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="emailTemplateFlagSenderReadOnly"
                >
                  Sender is read only
                </label>
                <div className="text-muted small">
                  When this template is used, the From Name and From Email
                  cannot be changed in the compose window.
                </div>
              </div>

              <div className={"d-flex justify-content-between mt-2"}></div>
            </Col>
          </Row>
    </AppModal>
  );
};

export default CreateEditEmailTemplate;
